import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  FilePlus,
  Folder,
  FolderInput,
  FolderPlus,
  Gauge,
  Library,
  Plus,
  Trash2,
  Upload,
} from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import type { KbDocument } from "@/api/generated/KbDocument"
import { describeError } from "@/api/mutation"
import {
  downloadKbDocument,
  useDeleteKbDocument,
  useDeleteKbFolder,
  useKbDocuments,
  useKbFolders,
  useKbStatus,
} from "@/api/queries/knowledge"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState, ErrorState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DocIcon, IndexStateBadge } from "@/features/knowledge/doc-bits"
import { DocumentSheet } from "@/features/knowledge/document-sheet"
import { FolderTree } from "@/features/knowledge/folder-tree"
import { IndexOverview, IndexStrip, useIndexJobWatcher } from "@/features/knowledge/index-panel"
import {
  ancestorsOf,
  buildFolderTree,
  childFolders,
  type FolderNode,
  fileName,
  formatBytes,
  parentFolder,
} from "@/features/knowledge/kb-logic"
import { MoveDialog, NewDocumentDialog, NewFolderDialog } from "@/features/knowledge/path-dialogs"
import { FileDropArea, UploadDialog } from "@/features/knowledge/upload"

interface KnowledgeSearch {
  tab?: "index"
  folder?: string
  q?: string
  doc?: number
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined)

export const Route = createFileRoute("/_app/knowledge/")({
  staticData: { titleKey: "nav.items.documents" },
  validateSearch: (s: Record<string, unknown>): KnowledgeSearch => ({
    tab: s.tab === "index" ? "index" : undefined,
    folder: str(s.folder),
    q: str(s.q),
    doc: Number(s.doc) > 0 ? Number(s.doc) : undefined,
  }),
  component: KnowledgePage,
})

/** A row of the documents table: a sub-folder or a document. */
type Entry = { kind: "folder"; path: string; count: number } | { kind: "doc"; doc: KbDocument }

const entryName = (e: Entry) => (e.kind === "folder" ? fileName(e.path) : fileName(e.doc.path))

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value)
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

function KnowledgePage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setSearch = (patch: Partial<KnowledgeSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const folder = search.folder ?? ""
  const tab = search.tab ?? "documents"
  const [query, setQuery] = React.useState(search.q ?? "")
  const debounced = useDebounced(query.trim(), 300)
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the debounced value
  React.useEffect(() => {
    if ((debounced || undefined) !== search.q) setSearch({ q: debounced || undefined })
  }, [debounced])

  const status = useKbStatus()
  useIndexJobWatcher(status.data)
  const embeddingsConfigured = !!status.data?.embedding.configured
  const folders = useKbFolders()
  const docs = useKbDocuments({ folder, q: search.q, recursive: !!search.q })
  const tree = React.useMemo(() => buildFolderTree(folders.data ?? []), [folders.data])
  const folderExists = !folder || !!folders.data?.some((f) => f.path === folder)

  // dialogs
  const [upload, setUpload] = React.useState<{ open: boolean; folder: string; files?: File[] }>({
    open: false,
    folder: "",
  })
  const [newFolder, setNewFolder] = React.useState<{ open: boolean; parent: string }>({
    open: false,
    parent: "",
  })
  const [newDoc, setNewDoc] = React.useState(false)
  const [moving, setMoving] = React.useState<{ path: string; kind: "document" | "folder" } | null>(null)
  const [treeOpen, setTreeOpen] = React.useState(false)
  const confirm = useConfirm()
  const deleteDoc = useDeleteKbDocument()
  const deleteFolder = useDeleteKbFolder()

  const openFolder = (f: string) => {
    setQuery("")
    setSearch({ folder: f || undefined, q: undefined })
    setTreeOpen(false)
  }

  const download = (d: KbDocument) =>
    void downloadKbDocument(d).catch((e: unknown) => toast.error(describeError(e, t)))

  const confirmDeleteDoc = (d: KbDocument) =>
    void confirm({
      title: t("knowledge.documents.deleteTitle", { name: fileName(d.path) }),
      description: t("knowledge.documents.deleteDescription"),
      confirmLabel: t("knowledge.actions.delete"),
      destructive: true,
      onConfirm: async () => {
        await deleteDoc.mutateAsync(d)
        if (search.doc === d.id) setSearch({ doc: undefined })
      },
    })

  const confirmDeleteFolder = (f: Pick<FolderNode, "path" | "count">) =>
    void confirm({
      title: t("knowledge.folders.deleteTitle", { name: fileName(f.path) }),
      description:
        f.count > 0
          ? t("knowledge.folders.deleteWithDocs", { count: f.count })
          : t("knowledge.folders.deleteEmpty"),
      confirmLabel: t("knowledge.folders.delete"),
      destructive: true,
      confirmText: f.count > 0 ? fileName(f.path) : undefined,
      onConfirm: async () => {
        await deleteFolder.mutateAsync({ path: f.path })
        if (folder === f.path || folder.startsWith(`${f.path}/`)) openFolder(parentFolder(f.path))
      },
    })

  const treeActions = {
    onNewFolder: (parent: string) => setNewFolder({ open: true, parent }),
    onUpload: (f: string) => setUpload({ open: true, folder: f }),
    onMove: (f: string) => setMoving({ path: f, kind: "folder" }),
    onDelete: confirmDeleteFolder,
    onDropFiles: (f: string, files: File[]) => setUpload({ open: true, folder: f, files }),
  }

  const entries = React.useMemo<Entry[]>(() => {
    const subfolders: Entry[] = search.q
      ? []
      : childFolders(folders.data ?? [], folder).map((f) => ({
          kind: "folder",
          path: f.path,
          count: f.document_count,
        }))
    return [...subfolders, ...(docs.data?.items ?? []).map((doc): Entry => ({ kind: "doc", doc }))]
  }, [folders.data, folder, docs.data, search.q])

  const columns = React.useMemo(
    () =>
      createColumns<Entry>((c) => [
        c.accessor((e) => `${e.kind === "folder" ? 0 : 1}${entryName(e).toLowerCase()}`, {
          id: "name",
          header: t("knowledge.documents.columns.name"),
          enableHiding: false,
          cell: ({ row }) => {
            const e = row.original
            if (e.kind === "folder")
              return (
                <div className="flex min-w-48 items-center gap-2.5">
                  <Folder className="text-primary/80 size-4 shrink-0" />
                  <span className="truncate font-medium">{fileName(e.path)}</span>
                </div>
              )
            const d = e.doc
            const name = fileName(d.path)
            const sub = search.q ? parentFolder(d.path) : d.title && d.title !== name ? d.title : ""
            return (
              <div className="flex max-w-xl min-w-48 items-center gap-2.5">
                <DocIcon path={d.path} />
                <div className="min-w-0">
                  <div className="truncate font-medium">{name}</div>
                  {sub && <div className="text-muted-foreground truncate text-xs">{sub}</div>}
                </div>
              </div>
            )
          },
        }),
        c.accessor((e) => (e.kind === "doc" ? (e.doc.indexed ? 2 : e.doc.index_error ? 0 : 1) : -1), {
          id: "index",
          header: t("knowledge.documents.columns.index"),
          meta: { label: t("knowledge.documents.columns.index") },
          cell: ({ row }) =>
            row.original.kind === "doc" ? (
              <IndexStateBadge doc={row.original.doc} embeddingsConfigured={embeddingsConfigured} />
            ) : (
              <span className="text-muted-foreground text-xs">
                {t("knowledge.documents.docCount", { count: row.original.count })}
              </span>
            ),
        }),
        c.accessor((e) => (e.kind === "doc" ? e.doc.size_bytes : -1), {
          id: "size",
          header: t("knowledge.documents.columns.size"),
          meta: { align: "end", label: t("knowledge.documents.columns.size") },
          cell: ({ row }) =>
            row.original.kind === "doc" ? (
              <span className="text-muted-foreground text-xs tabular-nums">
                {formatBytes(row.original.doc.size_bytes, i18n.language)}
              </span>
            ) : null,
        }),
        c.accessor((e) => (e.kind === "doc" ? e.doc.updated_at : ""), {
          id: "updated",
          header: t("knowledge.documents.columns.updated"),
          meta: { label: t("knowledge.documents.columns.updated") },
          cell: ({ row }) =>
            row.original.kind === "doc" ? (
              <RelativeTime value={row.original.doc.updated_at} className="text-muted-foreground" />
            ) : null,
        }),
      ]),
    [t, i18n.language, embeddingsConfigured, search.q],
  )

  const empty = !search.q && entries.length === 0
  const total = folders.data && status.data ? status.data.total_docs : undefined

  const treePanel = folders.isError ? (
    <ErrorState variant="plain" error={folders.error} onRetry={() => void folders.refetch()} />
  ) : folders.isPending ? (
    <div className="space-y-2 p-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-6" style={{ width: `${60 + ((i * 13) % 35)}%` }} />
      ))}
    </div>
  ) : (
    <FolderTree tree={tree} total={total} selected={folder} onSelect={openFolder} actions={treeActions} />
  )

  return (
    <PageContainer width="full" className="max-w-[90rem]">
      <PageHeader
        title={t("knowledge.title")}
        description={t("knowledge.description")}
        icon={BookOpen}
        actions={
          tab === "documents" && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger render={<Button variant="outline" />}>
                  <Plus />
                  {t("knowledge.actions.new")}
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto min-w-48">
                  <DropdownMenuItem onClick={() => setNewFolder({ open: true, parent: folder })}>
                    <FolderPlus />
                    {t("knowledge.folders.new")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setNewDoc(true)}>
                    <FilePlus />
                    {t("knowledge.newDoc.action")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => setUpload({ open: true, folder })} data-testid="kb-upload">
                <Upload />
                {t("knowledge.upload.action")}
              </Button>
            </>
          )
        }
      >
        <Tabs value={tab} onValueChange={(v) => setSearch({ tab: v === "index" ? "index" : undefined })}>
          <TabsList variant="line" className="h-8">
            <TabsTrigger value="documents" className="px-2">
              <Library />
              {t("knowledge.tabs.documents")}
            </TabsTrigger>
            <TabsTrigger value="index" className="px-2">
              <Gauge />
              {t("knowledge.tabs.index")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>

      {tab === "index" ? (
        <IndexOverview />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-4 max-h-[calc(100dvh-6rem)] overflow-y-auto pr-1">{treePanel}</div>
          </aside>
          <div className="min-w-0 space-y-4">
            <IndexStrip status={status.data} />
            <Collapsible
              open={treeOpen}
              onOpenChange={setTreeOpen}
              className="bg-card rounded-xl border lg:hidden"
            >
              <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium">
                <Folder className="text-muted-foreground size-4" />
                <span className="truncate">{t("knowledge.tree.label")}</span>
                <ChevronRight
                  className={`ml-auto size-4 transition-transform ${treeOpen ? "rotate-90" : ""}`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="border-t p-2">{treePanel}</CollapsibleContent>
            </Collapsible>
            <Breadcrumbs folder={folder} onOpen={openFolder} />
            {!folderExists && folders.data ? (
              <EmptyState
                icon={Folder}
                title={t("knowledge.folders.missing.title")}
                description={t("knowledge.folders.missing.description", { path: folder })}
                action={
                  <Button size="sm" variant="outline" onClick={() => openFolder("")}>
                    {t("knowledge.tree.all")}
                  </Button>
                }
              />
            ) : (
              <FileDropArea
                onDrop={(files) => setUpload({ open: true, folder, files })}
                label={t("knowledge.upload.dropHere", { folder: folder || t("knowledge.folders.root") })}
              >
                <DataTable
                  label={t("knowledge.documents.title")}
                  data={docs.isPending || folders.isPending ? undefined : entries}
                  columns={columns}
                  getRowId={(e) => (e.kind === "folder" ? `f:${e.path}` : `d:${e.doc.id}`)}
                  loading={docs.isPending || folders.isPending}
                  error={docs.error}
                  onRetry={() => void docs.refetch()}
                  searchable
                  search={query}
                  onSearchChange={setQuery}
                  searchPlaceholder={
                    folder
                      ? t("knowledge.documents.searchIn", { folder: fileName(folder) })
                      : t("knowledge.documents.search")
                  }
                  pageSize={50}
                  columnToggle
                  persistKey="kb-documents"
                  isRowActive={(e) => e.kind === "doc" && e.doc.id === search.doc}
                  onRowClick={(e) =>
                    e.kind === "folder" ? openFolder(e.path) : setSearch({ doc: e.doc.id })
                  }
                  toolbarEnd={
                    !!docs.data?.total && (
                      <span className="text-muted-foreground text-xs tabular-nums">
                        {t("knowledge.documents.count", { count: docs.data.total })}
                      </span>
                    )
                  }
                  rowActions={(e) =>
                    e.kind === "folder" ? (
                      <>
                        <DropdownMenuItem onClick={() => openFolder(e.path)}>
                          <Folder />
                          {t("knowledge.actions.open")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setMoving({ path: e.path, kind: "folder" })}>
                          <FolderInput />
                          {t("knowledge.move.renameOrMove")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => confirmDeleteFolder({ path: e.path, count: e.count })}
                        >
                          <Trash2 />
                          {t("knowledge.folders.delete")}
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={() => setSearch({ doc: e.doc.id })}>
                          <Eye />
                          {t("knowledge.actions.open")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => download(e.doc)}>
                          <Download />
                          {t("knowledge.actions.download")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setMoving({ path: e.doc.path, kind: "document" })}>
                          <FolderInput />
                          {t("knowledge.actions.move")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={() => confirmDeleteDoc(e.doc)}>
                          <Trash2 />
                          {t("knowledge.actions.delete")}
                        </DropdownMenuItem>
                      </>
                    )
                  }
                  emptyState={
                    empty && (
                      <EmptyState
                        variant="plain"
                        icon={folder ? Folder : BookOpen}
                        title={
                          folder
                            ? t("knowledge.documents.emptyFolder.title")
                            : t("knowledge.documents.empty.title")
                        }
                        description={
                          folder
                            ? t("knowledge.documents.emptyFolder.description")
                            : t("knowledge.documents.empty.description")
                        }
                        action={
                          <Button size="sm" onClick={() => setUpload({ open: true, folder })}>
                            <Upload />
                            {t("knowledge.upload.action")}
                          </Button>
                        }
                        secondaryAction={
                          <Button size="sm" variant="outline" onClick={() => setNewDoc(true)}>
                            <FilePlus />
                            {t("knowledge.newDoc.action")}
                          </Button>
                        }
                      />
                    )
                  }
                />
              </FileDropArea>
            )}
          </div>
        </div>
      )}

      <DocumentSheet
        id={search.doc}
        onClose={() => setSearch({ doc: undefined })}
        onOpen={(id) => setSearch({ doc: id })}
        onMove={(d) => setMoving({ path: d.path, kind: "document" })}
        embeddingsConfigured={embeddingsConfigured}
      />
      <UploadDialog
        open={upload.open}
        onOpenChange={(o) => setUpload((u) => ({ ...u, open: o }))}
        folder={upload.folder}
        initialFiles={upload.files}
        folders={folders.data ?? []}
      />
      <NewFolderDialog
        open={newFolder.open}
        onOpenChange={(o) => setNewFolder((f) => ({ ...f, open: o }))}
        parent={newFolder.parent}
        folders={folders.data ?? []}
        onCreated={openFolder}
      />
      <NewDocumentDialog
        open={newDoc}
        onOpenChange={setNewDoc}
        parent={folder}
        folders={folders.data ?? []}
        onCreated={(id) => setSearch({ doc: id })}
      />
      <MoveDialog
        target={moving}
        onOpenChange={(o) => !o && setMoving(null)}
        folders={folders.data ?? []}
        onMoved={(to) => {
          // follow a moved folder that was open (or contained the open folder)
          if (moving?.kind === "folder" && (folder === moving.path || folder.startsWith(`${moving.path}/`)))
            openFolder(to + folder.slice(moving.path.length))
        }}
      />
    </PageContainer>
  )
}

function Breadcrumbs({ folder, onOpen }: { folder: string; onOpen: (f: string) => void }) {
  const { t } = useTranslation()
  if (!folder) return null
  const crumbs = [...ancestorsOf(folder), folder]
  return (
    <nav aria-label={t("knowledge.tree.breadcrumbs")} className="flex flex-wrap items-center gap-1 text-sm">
      <button
        type="button"
        onClick={() => onOpen("")}
        className="text-muted-foreground hover:text-foreground"
      >
        {t("knowledge.tree.all")}
      </button>
      {crumbs.map((c, i) => (
        <span key={c} className="inline-flex items-center gap-1">
          <ChevronRight className="text-muted-foreground size-3.5" aria-hidden />
          {i === crumbs.length - 1 ? (
            <span className="font-medium" aria-current="page">
              {fileName(c)}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onOpen(c)}
              className="text-muted-foreground hover:text-foreground"
            >
              {fileName(c)}
            </button>
          )}
        </span>
      ))}
    </nav>
  )
}
